/**
 * Expense Recipients API module.
 *
 * Receptor externo de gastos (`tipo: 5` en `pagado_por`/`pagado_a` de la tabla
 * `gastos`): cualquier OTRA EMPRESA que recibe dinero saliente de Aloja Fácil y
 * NO es usuaria del sistema — servicios básicos (internet, agua), impuestos,
 * tiendas y proveedores de insumos, servicios profesionales (por ejemplo, un
 * contador), más el receptor semilla "Otro". Son solo una clasificación del
 * flujo de dinero: no tienen sesión ni finanzas propias.
 *
 * Una LAVANDERÍA nunca es un receptor externo, ni la General Externa ni una AF
 * interna. Su plata vive en la tabla `lavados` y se gestiona desde /lavanderia;
 * registrarla acá como gasto la vuelve invisible para la reconciliación de ese
 * módulo y la cuenta dos veces en la utilidad. El backend rechaza el alta de un
 * receptor cuyo nombre coincida con una lavandería del catálogo.
 *
 * Ojo con el código `tipo: 5`: está sobrecargado y su significado lo decide la
 * tabla, no el número. En `gastos` es este receptor externo (trae
 * `recipient_id` → `expense_recipients.id`, obligatorio); en `lavados` es una
 * lavandería del catálogo (trae `usuario_id` → `lavanderias.id`). El `tipo: 4`,
 * en cambio, es Trabajador (empleado sin login: mantención, supervisor,
 * contador) — se rotulaba "Otro" hasta el round 2026-04-30 decies y ese nombre
 * ya no se usa, porque además choca con el receptor semilla "Otro" (tipo 5).
 *
 * Todos los endpoints requieren rol `administrador` en el backend.
 */

import { apiFetch } from "./apiClient";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExpenseRecipient {
  id: number;
  nombre: string;
  categoria: string | null;
  notas: string | null;
  activo: boolean;
  fecha_creacion: string;
  fecha_actualizacion: string;
}

export interface CreateExpenseRecipientPayload {
  nombre: string;
  categoria?: string | null;
  notas?: string | null;
}

export type UpdateExpenseRecipientPayload = Partial<
  Pick<ExpenseRecipient, "nombre" | "categoria" | "notas" | "activo">
>;

export interface DeleteExpenseRecipientResponse {
  ok: true;
  mode: "soft" | "hard";
}

// ─── API ──────────────────────────────────────────────────────────────────────

// El backend (expenseRecipient.controller.js) responde SIEMPRE envuelto
// en `{ data: ... }` (líneas 22, 128, 133). El frontend usa los recipients
// como array (`.find()`, `.filter()`), por eso desenvolvemos acá. Sin esto,
// `useMemo(() => recipients.find(...))` en finanzasSection.tsx revienta con
// "z.find is not a function" y el ErrorBoundary muestra "No se pudo cargar".

export async function listExpenseRecipients(
  includeInactive = false,
): Promise<ExpenseRecipient[] | null> {
  const qs = includeInactive ? "?includeInactive=true" : "";
  const res = await apiFetch<{ data: ExpenseRecipient[] } | ExpenseRecipient[]>(
    `/finanzas/empresa/recipients${qs}`,
    { errorMsg: "Error al obtener receptores" },
  );
  if (!res) return null;
  // Defensivo: aceptamos tanto el formato envuelto (actual) como el plano
  // (por si en el futuro el backend se simplifica).
  if (Array.isArray(res)) return res;
  return Array.isArray(res.data) ? res.data : [];
}

export async function createExpenseRecipient(
  payload: CreateExpenseRecipientPayload,
): Promise<ExpenseRecipient | null> {
  const res = await apiFetch<{ data: ExpenseRecipient } | ExpenseRecipient>(
    "/finanzas/empresa/recipients",
    {
      method: "POST",
      body: JSON.stringify(payload),
      successMsg: "Receptor creado",
      errorMsg: "Error al crear el receptor",
    },
  );
  if (!res) return null;
  return "data" in res && res.data ? res.data : (res as ExpenseRecipient);
}

export async function updateExpenseRecipient(
  id: number,
  payload: UpdateExpenseRecipientPayload,
): Promise<ExpenseRecipient | null> {
  const res = await apiFetch<{ data: ExpenseRecipient } | ExpenseRecipient>(
    `/finanzas/empresa/recipients/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
      successMsg: "Receptor actualizado",
      errorMsg: "Error al actualizar el receptor",
    },
  );
  if (!res) return null;
  return "data" in res && res.data ? res.data : (res as ExpenseRecipient);
}

export async function deleteExpenseRecipient(
  id: number,
): Promise<DeleteExpenseRecipientResponse | null> {
  return apiFetch<DeleteExpenseRecipientResponse>(
    `/finanzas/empresa/recipients/${id}`,
    {
      method: "DELETE",
      errorMsg: "Error al eliminar el receptor",
    },
  );
}

export const expenseRecipientsApi = {
  list: listExpenseRecipients,
  create: createExpenseRecipient,
  update: updateExpenseRecipient,
  remove: deleteExpenseRecipient,
};
